import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaExternalLinkAlt, FaGithub } from 'react-icons/fa';

const projects = [
  {
    id: 1,
    title: 'Duabooth',
    client: 'DUABOOTH',
    description:
      'A modern real-time photobooth application with mic toggle, multiple filters, multiple strip layouts, and an easy-to-use interface — letting partners and friends take photos together across distances.',
    image: '/duabooth.png',
    technologies: ['WebRTC', 'Socket.io', 'React', 'Canvas API'],
    liveLink: 'https://duabooth.site/',
    tag: 'Flagship',
    metrics: [
      { value: '50K+', label: 'USERS IN 2 MONTHS' },
      { value: '20%', label: 'RETURN USER RATE' },
    ],
  },
  {
    id: 2,
    title: 'Decon PSI',
    client: 'EDUCATION',
    description:
      'An AI chatbot that creates, analyzes, and summarizes text — specially built for students to cut through study load.',
    image: '/decon.png',
    technologies: ['Next.js', 'React', 'Tailwind CSS', 'Node.js'],
    liveLink: 'https://decon-psi.vercel.app',
    tag: 'AI Tool',
    metrics: [
      { value: 'AI', label: 'STUDY ASSISTANT' },
      { value: 'LIVE', label: 'DEPLOYMENT' },
    ],
  },
  {
    id: 3,
    title: 'MuskAI',
    client: 'AI DEVTOOL',
    description:
      'An advanced AI code generation tool that transforms ideas into functional code across multiple programming languages — including a sophisticated math solver that breaks down problems step-by-step.',
    image: '/muskai.png',
    technologies: ['TypeScript', 'React', 'Python', 'Machine Learning'],
    liveLink: 'https://changeai.onrender.com',
    tag: 'AI Devtool',
    metrics: [
      { value: 'MULTI', label: 'LANGUAGE GEN' },
      { value: 'LIVE', label: 'DEPLOYMENT' },
    ],
  },
  {
    id: 4,
    title: 'Deepsquid',
    client: 'CANVAS ASSISTANT',
    description:
      'A Canvas-connected web assistant that links to a student account, pulls school updates into one place, and lets students chat with their academic data through a guided assistant interface.',
    image: '/deepsquid.png',
    technologies: ['React', 'Canvas API', 'AI Assistant', 'Dashboard'],
    liveLink: 'https://deepsquid-topaz.vercel.app/',
    tag: 'Education',
    metrics: [
      { value: 'LMS', label: 'INTEGRATION' },
      { value: 'LIVE', label: 'DEPLOYMENT' },
    ],
  },
  {
    id: 5,
    title: 'Ricky Roaster',
    client: 'AI TOOL',
    description:
      'An AI-powered roaster that analyzes uploaded photos and delivers witty, personalized roasts. Upload your image and let Rick roast you with clever AI-generated humor.',
    image: '/rick.png',
    technologies: ['React', 'AI/ML', 'Image Processing', 'OpenAI'],
    liveLink: 'https://ricky-sable.vercel.app/',
    tag: 'AI Tool',
    metrics: [
      { value: 'IMAGE', label: 'INPUT + LLM' },
      { value: 'LIVE', label: 'DEPLOYMENT' },
    ],
  },
  {
    id: 6,
    title: 'ChatMaybe',
    client: 'CAMPUS CHAT',
    description:
      'A platform where students connect with peers from their school. Users sign in with their school Gmail and chat with peers who may or may not attend the same institution.',
    image: '/chat.png',
    technologies: ['React', 'Firebase', 'Node.js', 'Express'],
    liveLink: 'https://chatmaybe.onrender.com',
    tag: 'Social',
    metrics: [
      { value: 'GMAIL', label: 'SSO AUTH' },
      { value: 'LIVE', label: 'DEPLOYMENT' },
    ],
  },
  {
    id: 7,
    title: 'SocioFix',
    client: 'SUPPORT',
    description:
      'A versatile communication platform where users connect with support agents, administrators, AI assistants, or peers. Built for secure and anonymous support.',
    image: '/sociofix.png',
    technologies: ['React', 'Socket.io', 'Node.js', 'MongoDB'],
    liveLink: 'https://sociofix.onrender.com',
    tag: 'Support',
    metrics: [
      { value: 'REALTIME', label: 'CHAT ROOMS' },
      { value: 'LIVE', label: 'DEPLOYMENT' },
    ],
  },
  {
    id: 8,
    title: 'WNM Showcase',
    client: 'MARKETING',
    description:
      'A modern web application showcasing WNM services and capabilities — responsive design, smooth animations, and an intuitive user interface.',
    image: '/demo.png',
    technologies: ['React', 'CSS3', 'JavaScript', 'Responsive Design'],
    liveLink: 'https://demo-olive-tau.vercel.app/',
    tag: 'Showcase',
    metrics: [
      { value: 'MKT', label: 'LANDING SITE' },
      { value: 'LIVE', label: 'DEPLOYMENT' },
    ],
  },
  {
    id: 9,
    title: 'Payment Gateway Demo',
    client: 'PAYMENTS',
    description:
      'A comprehensive payment gateway demonstration using HitPay API integration — secure payment processing, transaction management, and real-time payment status updates.',
    image: '/hitpay.png',
    technologies: ['React', 'Node.js', 'HitPay API', 'Express'],
    liveLink: '#',
    tag: 'Payments',
    metrics: [
      { value: 'HITPAY', label: 'API INTEGRATION' },
      { value: 'DEMO', label: 'INTERACTIVE' },
    ],
  },
  {
    id: 10,
    title: 'SaveMySlip',
    client: 'OPERATIONS',
    description:
      'A boat dock reservation system for marina owners and HOAs — real-time availability tracking, rule enforcement, boat registration, and admin dashboards.',
    image: '/savemyslip.png',
    technologies: ['React', 'Node.js', 'MongoDB', 'Express'],
    status: 'Unavailable',
    tag: 'Operations',
    metrics: [
      { value: '1 WEEK', label: 'DELIVERY' },
      { value: 'PRIVATE', label: 'INTERNAL USE' },
    ],
  },
];

const cardGradients = [
  'linear-gradient(rgba(255, 173, 173, 0.62) 0%, rgba(255, 173, 173, 0.4) 100%)',
  'linear-gradient(rgba(202, 187, 233, 0.42) 0%, rgba(202, 187, 233, 0.64) 100%)',
  'linear-gradient(rgba(188, 232, 204, 0.58) 0%, rgba(215, 241, 226, 0.8) 100%)',
  'linear-gradient(rgba(173, 219, 255, 0.5) 0%, rgba(205, 235, 255, 0.72) 100%)',
  'linear-gradient(rgba(255, 214, 165, 0.58) 0%, rgba(255, 229, 194, 0.78) 100%)',
  'linear-gradient(rgba(189, 224, 254, 0.55) 0%, rgba(205, 238, 255, 0.78) 100%)',
  'linear-gradient(rgba(255, 198, 221, 0.55) 0%, rgba(255, 222, 236, 0.76) 100%)',
  'linear-gradient(rgba(253, 240, 174, 0.58) 0%, rgba(255, 247, 205, 0.82) 100%)',
  'linear-gradient(rgba(198, 226, 255, 0.55) 0%, rgba(225, 240, 255, 0.78) 100%)',
  'linear-gradient(rgba(205, 193, 255, 0.5) 0%, rgba(230, 224, 255, 0.76) 100%)',
];

const Section = styled.section`
  padding: 100px 72px;
  background: #fff;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 56px 28px 72px;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 52px;

  @media (max-width: 768px) {
    margin-bottom: 28px;
  }
`;

const Title = styled(motion.h2)`
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: 700;
  font-size: clamp(2.6rem, 6vw, 5.6rem);
  line-height: 0.98;
  letter-spacing: -0.055em;
  color: #2d2b2a;
  margin: 0;

  @media (max-width: 768px) {
    font-size: clamp(2.8rem, 11vw, 4.5rem);
  }
`;

const CaseStudies = styled.div`
  display: flex;
  flex-direction: column;
  gap: 72px;

  @media (max-width: 768px) {
    gap: 28px;
  }
`;

const CaseCard = styled(motion.article)`
  position: relative;
  min-height: 491px;
  padding: 64px;
  border-radius: 24px;
  overflow: visible;
  background: ${({ $gradient }) => $gradient};
  isolation: isolate;
  display: grid;
  grid-template-columns: minmax(340px, 0.92fr) minmax(420px, 1.08fr);
  align-items: center;
  justify-content: center;
  gap: 64px;

  @media (max-width: 768px) {
    min-height: 385px;
    padding: 40px;
    grid-template-columns: minmax(0, 1fr) minmax(280px, 0.95fr);
    gap: 40px;
    border-radius: 24px;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    padding: 32px 28px;
    gap: 28px;
  }
`;

const Media = styled.div`
  position: relative;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: stretch;

  &::before {
    content: '';
    position: absolute;
    width: 72%;
    height: 72%;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.35);
    filter: blur(18px);
  }

  img {
    position: relative;
    width: min(100%, 520px);
    max-height: 330px;
    object-fit: contain;
    border-radius: 18px;
    filter: drop-shadow(0 18px 22px rgba(45, 43, 42, 0.22));
    transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }

  ${CaseCard}:hover & img {
    transform: translateY(-6px) scale(1.02);
  }

  @media (max-width: 768px) {
    min-height: 240px;

    img {
      max-height: 230px;
      width: min(100%, 420px);
    }
  }

  @media (max-width: 620px) {
    min-height: 190px;

    img {
      max-height: 190px;
    }
  }
`;

const Copy = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  color: #2d2b2a;

  @media (max-width: 900px) {
    gap: 20px;
  }
`;

const Client = styled.div`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: clamp(0.9rem, 1.1vw, 1.05rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  color: #4e4b49;
`;

const CaseTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: 500;
  font-size: clamp(2.45rem, 3.7vw, 3.65rem);
  line-height: 1.08;
  letter-spacing: -0.045em;
  color: #2d2b2a;
  margin: 0;
  max-width: 570px;

  @media (max-width: 768px) {
    font-size: clamp(2.15rem, 5.5vw, 3.2rem);
    max-width: 420px;
  }
`;

const Description = styled.p`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: clamp(1rem, 1.28vw, 1.2rem);
  line-height: 1.45;
  letter-spacing: -0.01em;
  color: #3b3836;
  margin: 0;
  max-width: 560px;

  @media (max-width: 768px) {
    font-size: 0.98rem;
    line-height: 1.45;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 0;
`;

const LinkBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 49px;
  padding: 12px 23px 10px;
  background: #fff;
  color: #2d2b2a;
  border-radius: 999px;
  border: 2px solid #2d2b2a;
  border-right-width: 4px;
  border-bottom-width: 4px;
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 0;
  text-transform: none;
  transition: transform 0.25s ease, background 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.86);
    transform: translate(1px, 1px);
  }

  svg {
    display: none;
  }

  @media (max-width: 768px) {
    min-height: 44px;
    padding: 11px 20px 9px;
    font-size: 0.92rem;
  }
`;

const GhostBtn = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 49px;
  padding: 12px 23px 10px;
  background: rgba(255, 255, 255, 0.7);
  color: rgba(45, 43, 42, 0.72);
  border-radius: 999px;
  border: 2px solid rgba(45, 43, 42, 0.72);
  border-right-width: 4px;
  border-bottom-width: 4px;
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 0;
  text-transform: none;

  svg {
    display: none;
  }
`;

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(120px, 1fr));
  gap: 22px;
  width: min(100%, 440px);
  margin-top: 2px;

  @media (max-width: 768px) {
    gap: 14px;
  }
`;

const Metric = styled.div`
  color: #2d2b2a;

  strong {
    display: block;
    font-size: clamp(1.65rem, 2.3vw, 2.35rem);
    line-height: 1;
    font-weight: 500;
    letter-spacing: -0.04em;
  }

  span {
    display: block;
    margin-bottom: 8px;
    font-size: 0.82rem;
    line-height: 1.15;
    font-weight: 800;
    letter-spacing: -0.01em;
  }
`;

const Projects = () => {
  return (
    <Section id="projects">
      <Container>
        <Header>
          <Title
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Recent work
          </Title>
        </Header>

        <CaseStudies>
          {projects.map((project, i) => (
            <CaseCard
              key={project.id}
              $gradient={cardGradients[i % cardGradients.length]}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true, margin: '-80px' }}
            >
              <Copy className="case-copy">
                <Client>{project.client}</Client>
                <CaseTitle>{project.title}</CaseTitle>
                <Description>{project.description}</Description>
                <Actions>
                  {project.status === 'Unavailable' ? (
                    <GhostBtn>
                      <FaGithub />
                      Coming soon
                    </GhostBtn>
                  ) : project.liveLink === '#' ? (
                    <GhostBtn>
                      <FaExternalLinkAlt />
                      Coming soon
                    </GhostBtn>
                  ) : (
                    <LinkBtn href={project.liveLink} target="_blank" rel="noopener noreferrer">
                      Go to project
                      <FaExternalLinkAlt />
                    </LinkBtn>
                  )}
                </Actions>
              </Copy>
              <Media className="case-media">
                <img src={project.image} alt={project.title} loading="lazy" />
              </Media>
              <Metrics aria-label={`${project.title} project highlights`}>
                {project.metrics.map((metric) => (
                  <Metric key={`${project.id}-${metric.label}`}>
                    <span>{metric.label}</span>
                    <strong>{metric.value}</strong>
                  </Metric>
                ))}
              </Metrics>
            </CaseCard>
          ))}
        </CaseStudies>
      </Container>
    </Section>
  );
};

export default Projects;
